import { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Checkbox, Flex, Input, InputGroup, InputLeftElement, Modal, ModalBody,
  ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Spinner, Text, useToast,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { attachMovementsToGoal, searchMovements } from '../../api/endpointsBackend';
import { formatAmount } from '../../utils/goals';
import { formatDate } from '../../utils/intlFormatters';

const CANDIDATE_DAYS = 50;

/**
 * Attach movements the goal does not have yet.
 *
 * Offers what the goal's own accounts touched and has no goal, which is where
 * a goal built out of past movements comes from. The attach endpoint decides:
 * it takes what the rules allow and reports the rest, and this reports both.
 */
export default function GoalAttachModal({ goal, isOpen, onClose, onAttached }) {
  const { t } = useTranslation('goals');
  const toast = useToast();
  const [movements, setMovements] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const accountIds = useMemo(() => (goal?.accounts || []).map((a) => a.account_id), [goal]);

  useEffect(() => {
    if (!isOpen || accountIds.length === 0) return undefined;
    let active = true;
    setLoading(true);
    setSelected(new Set());
    searchMovements(
      [
        { entity: 'Movement', column: 'account_id', operator: 'in', values: accountIds },
        { entity: 'Goal', column: 'id', operator: 'is_null', values: [true] },
      ],
      null, null, null, CANDIDATE_DAYS, 0,
    )
      .then((data) => { if (active) setMovements(data?.items || []); })
      .catch(() => { if (active) setMovements([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isOpen, accountIds]);

  const visible = search
    ? movements.filter((m) => `${m.note || ''} ${m.description || ''}`.toLowerCase().includes(search.toLowerCase()))
    : movements;

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAttach = async () => {
    setSaving(true);
    try {
      const result = await attachMovementsToGoal(goal.id, [...selected]);
      const skipped = result.skipped || [];
      toast({
        title: t('bulk.result', {
          attached: (result.attached || []).length,
          name: goal.name,
          skipped: skipped.length,
        }),
        description: skipped.length ? (
          <Box>
            {skipped.map((item) => (
              <Text key={item.movement_id} fontSize="xs">
                {t(`reasons.${item.reason}`, { defaultValue: item.reason })}
              </Text>
            ))}
          </Box>
        ) : undefined,
        status: skipped.length ? 'warning' : 'success',
        duration: skipped.length ? 8000 : 2500,
        isClosable: true,
      });
      onAttached();
      onClose();
    } catch (err) {
      toast({ title: t('toasts.errorTitle'), description: t('toasts.saveFailed'), status: 'error', duration: 5000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">{t('movements.attachTitle', { name: goal?.name })}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <InputGroup size="sm" mb={3}>
            <InputLeftElement pointerEvents="none" h="32px"><SearchIcon color="redesign.text.muted" boxSize={3} /></InputLeftElement>
            <Input placeholder={t('movements.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} h="32px" borderRadius="md" fontSize="sm" />
          </InputGroup>
          {loading ? (
            <Flex justify="center" py={8}><Spinner /></Flex>
          ) : visible.length === 0 ? (
            <Text fontSize="sm" color="redesign.text.muted" py={6} textAlign="center">
              {t('movements.noCandidates')}
            </Text>
          ) : (
            <Box maxH="320px" overflowY="auto" overflowX="hidden">
              {visible.map((movement) => (
                <Flex key={movement.id} align="center" gap={3} py={2} px={1} borderRadius="md" _hover={{ bg: 'redesign.bg.hover' }}>
                  <Checkbox
                    size="sm"
                    isChecked={selected.has(movement.id)}
                    onChange={() => toggle(movement.id)}
                  />
                  <Box flex={1} minW={0}>
                    <Text fontSize="sm" color="redesign.heading" isTruncated>{movement.note || movement.description || '—'}</Text>
                    <Text fontSize="2xs" color="redesign.text.muted">{formatDate(movement.date)}</Text>
                  </Box>
                  <Text fontSize="sm" color="redesign.heading" flexShrink={0}>
                    <Text as="span" fontSize="2xs" color="redesign.text.muted" mr={1}>{movement.currency}</Text>
                    {formatAmount(movement.amount)}
                  </Text>
                </Flex>
              ))}
            </Box>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="outline" onClick={onClose}>{t('buttons.cancel')}</Button>
          <Button size="sm" variant="primary" onClick={handleAttach} isLoading={saving} isDisabled={selected.size === 0}>
            {t('movements.attachCount', { count: selected.size })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
